import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Stack,
  Button,
  Box,
  Collapse,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { settingsCache } from '@/services';
import { STRUCTURES, getStructureById } from '@/data/structures';
import type { StructureBonuses as StructureBonusesType, UserStructure } from '@/types';

export const StructureBonuses: React.FC = () => {
  const [bonuses, setBonuses] = useState<StructureBonusesType>({ structures: [] });
  const [saved, setSaved] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // New structure form
  const [selectedStructureId, setSelectedStructureId] = useState('');
  const [meBonusInput, setMeBonusInput] = useState('');

  // Load saved bonuses on mount
  useEffect(() => {
    const loadBonuses = async () => {
      const savedBonuses = await settingsCache.getStructureBonuses();
      // Ensure structures array exists
      const normalizedBonuses = {
        structures: savedBonuses.structures || [],
      };
      setBonuses(normalizedBonuses);
      // Always start collapsed
    };
    loadBonuses();
  }, []);

  const handleAddStructure = () => {
    if (!selectedStructureId || !meBonusInput) return;

    const meBonus = parseFloat(meBonusInput);
    if (isNaN(meBonus) || meBonus <= 0) return;

    const newStructure: UserStructure = {
      structureId: selectedStructureId,
      meBonus,
    };

    setBonuses((prev) => ({
      structures: [...(prev.structures || []), newStructure],
    }));

    setSelectedStructureId('');
    setMeBonusInput('');
    setSaved(false);
  };

  const handleRemoveStructure = (index: number) => {
    setBonuses((prev) => ({
      structures: (prev.structures || []).filter((_, i) => i !== index),
    }));
    setSaved(false);
  };

  const handleSave = async () => {
    try {
      await settingsCache.setStructureBonuses(bonuses);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Failed to save structure bonuses:', err);
    }
  };

  const handleClear = async () => {
    try {
      await settingsCache.clearStructureBonuses();
      setBonuses({ structures: [] });
      setSaved(false);
    } catch (err) {
      console.error('Failed to clear structure bonuses:', err);
    }
  };

  const hasAnyStructure = bonuses.structures?.length > 0;

  // Calculate total ME bonus for display
  const totalMEBonus = bonuses.structures?.reduce((sum, s) => sum + s.meBonus, 0) ?? 0;

  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h6">Manufacturing Structures</Typography>
              {hasAnyStructure && totalMEBonus > 0 && (
                <Typography variant="caption" color="success.main">
                  Total Bonus: -{totalMEBonus.toFixed(1)}% ME
                </Typography>
              )}
            </Box>
            <IconButton onClick={() => setExpanded(!expanded)} size="small">
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          <Collapse in={expanded}>
            <Stack spacing={3}>
              <Typography variant="body2" color="text.secondary">
                Add your citadel and manufacturing structures. The calculator will automatically use
                applicable bonuses. Citadel bonuses vary by item type.
              </Typography>

              {/* Current Structures List */}
              {hasAnyStructure && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Your Structures
                  </Typography>
                  <List dense>
                    {bonuses.structures?.map((structure, index) => {
                      const structureDef = getStructureById(structure.structureId);
                      return (
                        <ListItem
                          key={index}
                          secondaryAction={
                            <IconButton edge="end" onClick={() => handleRemoveStructure(index)}>
                              <DeleteIcon />
                            </IconButton>
                          }
                          sx={{ bgcolor: 'background.default', mb: 1, borderRadius: 1 }}
                        >
                          <ListItemText
                            primary={structureDef?.name ?? structure.structureId}
                            secondary={
                              <Box display="flex" gap={1} alignItems="center">
                                <Chip
                                  label={`-${structure.meBonus}% ME`}
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                />
                                {structureDef?.description && (
                                  <Typography variant="caption" color="text.secondary">
                                    {structureDef.description}
                                  </Typography>
                                )}
                              </Box>
                            }
                            secondaryTypographyProps={{ component: 'div' }}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              )}

              {/* Add New Structure */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Add Structure
                </Typography>
                <Box display="flex" gap={2} flexWrap="wrap">
                  <Box flex="1" minWidth="200px">
                    <FormControl fullWidth size="small">
                      <InputLabel>Structure</InputLabel>
                      <Select
                        value={selectedStructureId}
                        onChange={(e) => setSelectedStructureId(e.target.value)}
                        label="Structure"
                      >
                        {STRUCTURES.map((structure) => (
                          <MenuItem key={structure.id} value={structure.id}>
                            {structure.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  <Box width="120px">
                    <TextField
                      label="ME Bonus"
                      type="number"
                      size="small"
                      value={meBonusInput}
                      onChange={(e) => setMeBonusInput(e.target.value)}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                      inputProps={{
                        min: 0,
                        max: 100,
                        step: 0.5,
                      }}
                      fullWidth
                    />
                  </Box>
                  <Box>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleAddStructure}
                      disabled={!selectedStructureId || !meBonusInput}
                      size="large"
                    >
                      Add
                    </Button>
                  </Box>
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: 'block' }}
                >
                  Enter the ME bonus from your in-game structure panel (e.g., 5 for -5%)
                </Typography>
              </Box>

              {/* Save/Clear Buttons */}
              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  color={saved ? 'success' : 'primary'}
                  disabled={saved}
                >
                  {saved ? 'Saved!' : 'Save Structures'}
                </Button>
                {hasAnyStructure && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleClear}
                  >
                    Clear All
                  </Button>
                )}
              </Box>
            </Stack>
          </Collapse>
        </Stack>
      </CardContent>
    </Card>
  );
};
